(() => {
  'use strict';

  const shell = document.querySelector('[data-hpd-battery-modal]');
  if (!shell) return;

  const dialog = shell.querySelector('.hpd-battery-modal');
  const toolTitle = shell.querySelector('[data-hpd-battery-tool-title]');
  const toolImage = shell.querySelector('[data-hpd-battery-tool-image]');
  const toolBadge = shell.querySelector('[data-hpd-battery-tool-badge]');
  const compatibilityCopy = shell.querySelector('[data-hpd-battery-compatibility-copy]');
  const setContainer = shell.querySelector('[data-hpd-battery-sets]');
  const setCards = Array.from(shell.querySelectorAll('[data-hpd-battery-set]'));
  const emptyMessage = shell.querySelector('[data-hpd-battery-empty]');
  const addButton = shell.querySelector('[data-hpd-battery-add]');
  const bodyOnlyButton = shell.querySelector('[data-hpd-battery-body-only]');
  const selectionSummary = shell.querySelector('[data-hpd-battery-selection]');
  const status = shell.querySelector('[data-hpd-battery-status]');
  const closeButtons = Array.from(shell.querySelectorAll('[data-hpd-battery-close]'));
  const config = window.HPDTheme || {};
  const routes = config.routes || {};
  const bypassForms = new WeakSet();
  const bypassButtons = new WeakSet();
  const discountPercent = 5;

  let activeContext = null;
  let selectedCard = null;
  let lastFocused = null;
  let busy = false;
  let pairRepairBusy = false;

  function jsonRoute(route, fallback) {
    const value = route || fallback;
    return value.endsWith('.js') ? value : `${value}.js`;
  }

  function formatMoney(minorAmount) {
    const amount = Number(minorAmount || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: config.currency || 'GBP',
      }).format(amount);
    } catch (error) {
      return `£${amount.toFixed(2)}`;
    }
  }

  function pushEvent(name, detail = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...detail });
  }

  function createBundleId() {
    const randomPart = window.crypto?.getRandomValues
      ? Array.from(window.crypto.getRandomValues(new Uint32Array(2))).map((value) => value.toString(36)).join('')
      : Math.random().toString(36).slice(2);
    return `hpd-${Date.now().toString(36)}-${randomPart}`;
  }

  function compatibleWith(card, platform) {
    const setPlatform = card.dataset.hpdBatteryPlatform;
    if (platform === '18v') return setPlatform === '18v' || setPlatform === 'multivolt';
    return platform === '36v' && setPlatform === 'multivolt';
  }

  function getCompatibleCards(platform) {
    return setCards.filter((card) => compatibleWith(card, platform));
  }

  function normaliseLinePrice(amount, taxable) {
    const source = Number(amount || 0);
    if (!taxable) return { exVat: source, incVat: source };
    return config.taxesIncluded
      ? { exVat: Math.round(source / 1.2), incVat: source }
      : { exVat: source, incVat: Math.round(source * 1.2) };
  }

  function calculateBundlePrice(context, card) {
    const toolPricing = context.getToolPricing();
    const toolQuantity = context.getQuantity();
    const setPrice = Number(card.dataset.hpdBatteryPrice || 0);
    const setTaxable = card.dataset.hpdBatteryTaxable !== 'false';
    const toolSourceTotal = toolPricing.price * toolQuantity;
    const setSourceTotal = setPrice;
    const toolDiscounted = toolSourceTotal - Math.round(toolSourceTotal * discountPercent / 100);
    const setDiscounted = setSourceTotal - Math.round(setSourceTotal * discountPercent / 100);
    const normalTool = normaliseLinePrice(toolSourceTotal, toolPricing.taxable);
    const normalSet = normaliseLinePrice(setSourceTotal, setTaxable);
    const discountedTool = normaliseLinePrice(toolDiscounted, toolPricing.taxable);
    const discountedSet = normaliseLinePrice(setDiscounted, setTaxable);
    const normalExVat = normalTool.exVat + normalSet.exVat;
    const normalIncVat = normalTool.incVat + normalSet.incVat;
    const discountedExVat = discountedTool.exVat + discountedSet.exVat;
    const discountedIncVat = discountedTool.incVat + discountedSet.incVat;

    return {
      normalExVat,
      normalIncVat,
      discountedExVat,
      discountedIncVat,
      savingExVat: normalExVat - discountedExVat,
      savingIncVat: normalIncVat - discountedIncVat,
    };
  }

  function renderBundlePrices(context, cards) {
    cards.forEach((card) => {
      const pricing = calculateBundlePrice(context, card);
      card.hpdBundlePricing = pricing;
      card.querySelector('[data-hpd-bundle-price-ex]').textContent = `${formatMoney(pricing.discountedExVat)} ex VAT`;
      card.querySelector('[data-hpd-bundle-price-inc]').textContent = `${formatMoney(pricing.discountedIncVat)} inc VAT`;
      card.querySelector('[data-hpd-bundle-saving]').textContent = `Save ${formatMoney(pricing.savingExVat)} ex VAT`;
      card.querySelector('[data-hpd-bundle-normal]').textContent = formatMoney(pricing.normalExVat);
    });
  }

  function cleanToolTitle(title) {
    return String(title || '')
      .replace(/\s*[-–—]\s*(body[\s-]*only|bare unit)\s*$/i, '')
      .replace(/\s+(body[\s-]*only|bare unit)\s*$/i, '')
      .trim();
  }

  async function fetchCart() {
    const response = await fetch(jsonRoute(routes.cart, '/cart'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to check the cart.');
    return response.json();
  }

  function getBundleId(item) {
    return String(item?.properties?._hpd_bundle_id || '');
  }

  function getBundleRole(item) {
    return String(item?.properties?._hpd_bundle_role || '');
  }

  function withoutBundleProperties(properties = {}) {
    const remainingProperties = Object.fromEntries(Object.entries(properties).filter(([key]) => ![
      'Battery upgrade',
      '_hpd_bundle_id',
      '_hpd_bundle_role',
      '_hpd_bundle_discount_qty',
    ].includes(key)));

    // Shopify doesn't accept an empty properties object once a line has had
    // properties. This private marker keeps the line intact while ensuring the
    // customer-facing saving and MOD Discounts match are both removed.
    return { ...remainingProperties, _hpd_pair_expired: 'true' };
  }

  function orphanedBundleIds(cart) {
    const bundles = new Map();
    (Array.isArray(cart?.items) ? cart.items : []).forEach((item) => {
      const bundleId = getBundleId(item);
      if (!bundleId) return;
      if (!bundles.has(bundleId)) bundles.set(bundleId, new Set());
      bundles.get(bundleId).add(getBundleRole(item));
    });

    return Array.from(bundles.entries())
      .filter(([, roles]) => !roles.has('tool') || !roles.has('set'))
      .map(([bundleId]) => bundleId);
  }

  async function repairOrphanedBundles(cart) {
    if (pairRepairBusy || !Array.isArray(cart?.items)) return cart;
    const orphanIds = orphanedBundleIds(cart);
    if (!orphanIds.length) return cart;

    pairRepairBusy = true;
    let latestCart = cart;
    try {
      for (const bundleId of orphanIds) {
        let orphan = latestCart.items.find((item) => getBundleId(item) === bundleId);
        while (orphan) {
          const response = await fetch(jsonRoute(routes.cartChange, '/cart/change'), {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: orphan.key,
              quantity: orphan.quantity,
              properties: withoutBundleProperties(orphan.properties),
            }),
          });
          if (!response.ok) throw new Error('Unable to remove an incomplete battery-pair saving.');
          latestCart = await response.json();
          orphan = latestCart.items.find((item) => getBundleId(item) === bundleId);
        }
      }
    } catch (error) {
      return latestCart;
    } finally {
      pairRepairBusy = false;
    }

    window.dispatchEvent(new CustomEvent('cartcount:update', { detail: latestCart }));
    return latestCart;
  }

  function contextFromForm(form, submitter) {
    const variantInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    let variantPricing = [];
    try {
      variantPricing = JSON.parse(form.querySelector('.js-hpd-vat-payment-terms-data')?.textContent || '{}').variants || [];
    } catch (error) {
      variantPricing = [];
    }
    return {
      kind: 'form',
      source: form,
      submitter: submitter || form.querySelector('[data-product-atc]'),
      platform: form.dataset.hpdUpgradePlatform,
      title: form.dataset.hpdUpgradeTitle || '',
      image: form.dataset.hpdUpgradeImage || '',
      getVariantId: () => Number(variantInput?.value || 0),
      getQuantity: () => Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1),
      getToolPricing: () => {
        const variantId = Number(variantInput?.value || 0);
        const match = variantPricing.find((variant) => Number(variant.id) === variantId);
        return {
          price: Number(match?.priceMinor ?? form.dataset.hpdUpgradePrice ?? 0),
          taxable: match ? match.taxable !== false : form.dataset.hpdUpgradeTaxable !== 'false',
        };
      },
    };
  }

  function contextFromButton(button) {
    return {
      kind: 'button',
      source: button,
      submitter: button,
      platform: button.dataset.hpdUpgradePlatform,
      title: button.dataset.hpdUpgradeTitle || '',
      image: button.dataset.hpdUpgradeImage || '',
      getVariantId: () => Number(button.dataset.variantId || 0),
      getQuantity: () => 1,
      getToolPricing: () => ({
        price: Number(button.dataset.hpdUpgradePrice || 0),
        taxable: button.dataset.hpdUpgradeTaxable !== 'false',
      }),
    };
  }

  function continueOriginal(context) {
    if (!context?.source) return;
    if (context.kind === 'form') {
      bypassForms.add(context.source);
      if (typeof context.source.requestSubmit === 'function') {
        context.source.requestSubmit(context.submitter || undefined);
      } else {
        context.source.submit();
      }
      return;
    }

    bypassButtons.add(context.source);
    context.source.click();
  }

  function updateSelection(card) {
    selectedCard = card;
    setCards.forEach((item) => {
      const selected = item === card;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-checked', String(selected));
    });
    addButton.disabled = !selectedCard;
    status.textContent = '';
    if (!selectedCard) {
      selectionSummary.textContent = 'Select a power set to see your complete price.';
      return;
    }
    const pricing = selectedCard.hpdBundlePricing || calculateBundlePrice(activeContext, selectedCard);
    selectionSummary.innerHTML = `<strong>${formatMoney(pricing.discountedExVat)} ex VAT</strong> · ${formatMoney(pricing.discountedIncVat)} inc VAT · <span>5% saving applied</span>`;
  }

  function openModal(context, compatibleCards) {
    activeContext = context;
    lastFocused = context.submitter || context.source || document.activeElement;
    selectedCard = null;
    updateSelection(null);

    setCards.forEach((card) => {
      card.hidden = !compatibleCards.includes(card);
    });
    emptyMessage.hidden = compatibleCards.length > 0;
    setContainer.hidden = compatibleCards.length === 0;

    renderBundlePrices(context, compatibleCards);

    toolTitle.textContent = cleanToolTitle(context.title);
    toolImage.src = context.image || '';
    toolImage.alt = context.title ? `${context.title} product image` : '';
    toolImage.parentElement.hidden = !context.image;
    toolBadge.textContent = `${context.platform.toUpperCase()} body only`;
    compatibilityCopy.textContent = context.platform === '18v'
      ? 'Compatible with HiKOKI 18V and Multi Volt battery sets.'
      : 'Compatible with HiKOKI Multi Volt battery sets only.';

    status.textContent = '';
    selectionSummary.textContent = 'Select a power set to see your complete price.';
    shell.hidden = false;
    shell.inert = false;
    shell.setAttribute('aria-hidden', 'false');
    document.body.classList.add('hpd-battery-modal-open');
    window.requestAnimationFrame(() => {
      shell.classList.add('is-open');
      dialog.focus({ preventScroll: true });
    });

    pushEvent('hpd_battery_upgrade_view', {
      hpd_platform: context.platform,
      hpd_product_title: context.title,
      hpd_compatible_set_count: compatibleCards.length,
    });
  }

  function closeModal(restoreFocus = true) {
    if (busy) return;
    shell.classList.remove('is-open');
    shell.setAttribute('aria-hidden', 'true');
    shell.inert = true;
    shell.hidden = true;
    document.body.classList.remove('hpd-battery-modal-open');
    activeContext = null;
    selectedCard = null;
    if (restoreFocus && lastFocused instanceof HTMLElement && document.contains(lastFocused)) {
      lastFocused.focus({ preventScroll: true });
    }
  }

  async function intercept(context) {
    if (context.submitter?.getAttribute('aria-busy') === 'true') return;
    if (!context.platform || !['18v', '36v'].includes(context.platform)) {
      continueOriginal(context);
      return;
    }

    const compatibleCards = getCompatibleCards(context.platform);
    if (!compatibleCards.length) {
      continueOriginal(context);
      return;
    }

    openModal(context, compatibleCards);
  }

  async function addSelectedSetup() {
    if (!activeContext || !selectedCard || busy) return;

    const toolVariantId = activeContext.getVariantId();
    const setVariantId = Number(selectedCard.dataset.hpdBatteryVariantId || 0);
    const toolQuantity = activeContext.getQuantity();
    const bundleId = createBundleId();
    if (!toolVariantId || !setVariantId) {
      status.textContent = 'Please refresh the page and try again.';
      return;
    }

    busy = true;
    addButton.disabled = true;
    addButton.classList.add('is-loading');
    bodyOnlyButton.disabled = true;
    addButton.textContent = 'Adding both to cart…';
    status.textContent = '';

    try {
      const response = await fetch(jsonRoute(routes.cartAdd, '/cart/add'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: toolVariantId,
              quantity: toolQuantity,
              properties: {
                'Battery upgrade': '5% pair saving',
                _hpd_bundle_id: bundleId,
                _hpd_bundle_role: 'tool',
                _hpd_bundle_discount_qty: String(toolQuantity),
              },
            },
            {
              id: setVariantId,
              quantity: 1,
              properties: {
                'Battery upgrade': '5% pair saving',
                _hpd_bundle_id: bundleId,
                _hpd_bundle_role: 'set',
                _hpd_bundle_discount_qty: '1',
              },
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.description || errorPayload.message || 'Unable to add this setup to the cart.');
      }

      const completedContext = activeContext;
      const completedSet = selectedCard;
      const cart = await fetchCart();
      busy = false;
      closeModal(false);
      window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
      pushEvent('hpd_battery_upgrade_add', {
        hpd_platform: completedContext.platform,
        hpd_product_title: completedContext.title,
        hpd_battery_set_title: completedSet.dataset.hpdBatteryTitle || '',
        hpd_battery_set_variant_id: setVariantId,
        hpd_bundle_id: bundleId,
        hpd_discount_percent: discountPercent,
      });
    } catch (error) {
      status.textContent = error.message || 'Unable to add this setup. Please try again.';
    } finally {
      busy = false;
      addButton.classList.remove('is-loading');
      addButton.textContent = 'Add discounted setup';
      addButton.disabled = !selectedCard;
      bodyOnlyButton.disabled = false;
    }
  }

  function continueBodyOnly() {
    if (!activeContext || busy) return;
    const context = activeContext;
    pushEvent('hpd_battery_upgrade_body_only', {
      hpd_platform: context.platform,
      hpd_product_title: context.title,
    });
    closeModal(false);
    continueOriginal(context);
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.(
      'form[data-product-form][data-hpd-upgrade-platform="18v"], form[data-product-form][data-hpd-upgrade-platform="36v"]',
    );
    if (!form) return;
    if (bypassForms.has(form)) {
      bypassForms.delete(form);
      return;
    }
    if (event.submitter && !event.submitter.matches('[data-product-atc]')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    intercept(contextFromForm(form, event.submitter));
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-quick-buy][data-hpd-upgrade-platform]');
    if (!button || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (bypassButtons.has(button)) {
      bypassButtons.delete(button);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    intercept(contextFromButton(button));
  }, true);

  setCards.forEach((card) => card.addEventListener('click', () => updateSelection(card)));
  addButton.addEventListener('click', addSelectedSetup);
  bodyOnlyButton.addEventListener('click', continueBodyOnly);
  closeButtons.forEach((button) => button.addEventListener('click', () => closeModal(true)));

  window.addEventListener('cartcount:update', (event) => {
    if (Array.isArray(event.detail?.items)) repairOrphanedBundles(event.detail);
  });

  const runInitialPairCheck = async () => {
    try {
      const cart = await fetchCart();
      await repairOrphanedBundles(cart);
    } catch (error) {
      // Pair housekeeping must never interrupt normal shopping.
    }
  };
  if (Number(config.initialCartCount || 0) > 0) {
    window.setTimeout(runInitialPairCheck, 100);
  }

  document.addEventListener('keydown', (event) => {
    if (!shell.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal(true);
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from(dialog.querySelectorAll(
      'a[href], button:not([disabled]):not([hidden]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )).filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

})();
